// frontend/src/pages/TimeTracking/TimeEntryForm.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
    Form, Input, Button, Card, DatePicker, Select, InputNumber, 
    message, Row, Col, Divider, Space, Typography, Tag, Alert, 
    Badge, Table, Modal, Popconfirm, Tooltip, Checkbox 
} from 'antd';
import { 
    ClockCircleOutlined, SaveOutlined, RollbackOutlined, 
    PlusOutlined, UserOutlined, EditOutlined, DeleteOutlined, 
    ReloadOutlined, FileTextOutlined 
} from '@ant-design/icons';
import moment from 'moment';
import { 
    addTimeEntry, getCRsByTicket, addDirectTimeEntry, 
    updateTimeEntry, deleteTimeEntry, getAllTimeEntriesByTicket 
} from '../../utils/timeTrackingService';
import api from '../../utils/api';
import './TimeTrackingForm.css';

const { Title, Text } = Typography;
const { Option } = Select;

const TimeEntryForm = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [editForm] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [ticketInfo, setTicketInfo] = useState(null);
    const [crList, setCrList] = useState([]);
    const [loadingCRs, setLoadingCRs] = useState(false);
    const [selectedCR, setSelectedCR] = useState(null);
    
    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    
    const [savedCount, setSavedCount] = useState(0);
    const [shouldCloseAfterSave, setShouldCloseAfterSave] = useState(false);
    
    const [entries, setEntries] = useState([]);
    const [loadingEntries, setLoadingEntries] = useState(false);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [editingEntry, setEditingEntry] = useState(null);
    const [editLoading, setEditLoading] = useState(false);
    
    const [isDurationValid, setIsDurationValid] = useState(false);
    const [isEditDurationValid, setIsEditDurationValid] = useState(false);

    const ticketId = searchParams.get('ticketId');
    const crMasterId = searchParams.get('crMasterId');
    const mode = searchParams.get('mode') || 'cr';
    const isDirectMode = mode === 'direct';

    // ============================================================
    // WATCH TIME COMPONENTS (MAIN FORM)
    // ============================================================
    const startHour = Form.useWatch('start_hour', form);
    const startMinute = Form.useWatch('start_minute', form);
    const startAmPm = Form.useWatch('start_ampm', form);
    const endHour = Form.useWatch('end_hour', form);
    const endMinute = Form.useWatch('end_minute', form);
    const endAmPm = Form.useWatch('end_ampm', form);
    const endTimeNextDay = Form.useWatch('end_time_next_day', form);

    // ============================================================
    // AUTO-CALCULATE DURATION WITH NEXT DAY SUPPORT (MAIN FORM)
    // ============================================================
    useEffect(() => {
        const allStartFilled = startHour !== undefined && startHour !== null && 
                              startMinute !== undefined && startMinute !== null && 
                              startAmPm;
        const allEndFilled = endHour !== undefined && endHour !== null && 
                            endMinute !== undefined && endMinute !== null && 
                            endAmPm;
        
        if (allStartFilled && allEndFilled) {
            let startHour24 = parseInt(startHour);
            let endHour24 = parseInt(endHour);
            
            if (startAmPm === 'PM' && startHour24 !== 12) startHour24 += 12;
            if (startAmPm === 'AM' && startHour24 === 12) startHour24 = 0;
            if (endAmPm === 'PM' && endHour24 !== 12) endHour24 += 12;
            if (endAmPm === 'AM' && endHour24 === 12) endHour24 = 0;
            
            let totalStartMinutes = startHour24 * 60 + parseInt(startMinute);
            let totalEndMinutes = endHour24 * 60 + parseInt(endMinute);
            
            const isNextDay = endTimeNextDay === true;
            
            if (isNextDay) {
                totalEndMinutes += 1440;
            }
            
            const diffMinutes = totalEndMinutes - totalStartMinutes;
            
            if (diffMinutes > 0) {
                const hours = Math.floor(diffMinutes / 60);
                const minutes = diffMinutes % 60;
                const totalHours = parseFloat((hours + minutes / 60).toFixed(2));
                
                form.setFieldsValue({
                    hours: hours,
                    minutes: minutes,
                    duration: totalHours
                });
                setIsDurationValid(true);
                
                // Warn if duration is > 16 hours without next-day checkbox
                if (totalHours > 16 && !isNextDay) {
                    message.warning({
                        content: `Duration (${totalHours.toFixed(2)} hrs) is unusually long. If this spans multiple days, please check "End time is on the next day".`,
                        duration: 5,
                    });
                }
                
            } else if (diffMinutes === 0) {
                form.setFieldsValue({
                    hours: 0,
                    minutes: 0,
                    duration: 0
                });
                setIsDurationValid(false);
                message.warning('Start and end times are the same. Please adjust.');
            } else {
                form.setFieldsValue({
                    hours: 0,
                    minutes: 0,
                    duration: 0
                });
                setIsDurationValid(false);
                if (!isNextDay) {
                    message.warning('End time is before start time. If this spans to the next day, check the "End time is on the next day" box.');
                }
            }
        } else {
            form.setFieldsValue({
                hours: 0,
                minutes: 0,
                duration: 0
            });
            setIsDurationValid(false);
        }
    }, [startHour, startMinute, startAmPm, endHour, endMinute, endAmPm, endTimeNextDay, form]);

    // ============================================================
    // WATCH TIME COMPONENTS (EDIT MODAL)
    // ============================================================
    const editStartHour = Form.useWatch('start_hour', editForm);
    const editStartMinute = Form.useWatch('start_minute', editForm);
    const editStartAmPm = Form.useWatch('start_ampm', editForm);
    const editEndHour = Form.useWatch('end_hour', editForm);
    const editEndMinute = Form.useWatch('end_minute', editForm);
    const editEndAmPm = Form.useWatch('end_ampm', editForm);
    const editEndTimeNextDay = Form.useWatch('end_time_next_day', editForm);

    // ============================================================
    // AUTO-CALCULATE DURATION WITH NEXT DAY SUPPORT (EDIT MODAL)
    // ============================================================
    useEffect(() => {
        const allStartFilled = editStartHour !== undefined && editStartHour !== null && 
                              editStartMinute !== undefined && editStartMinute !== null && 
                              editStartAmPm;
        const allEndFilled = editEndHour !== undefined && editEndHour !== null && 
                            editEndMinute !== undefined && editEndMinute !== null && 
                            editEndAmPm;
        
        if (allStartFilled && allEndFilled) {
            let startHour24 = parseInt(editStartHour);
            let endHour24 = parseInt(editEndHour);
            
            if (editStartAmPm === 'PM' && startHour24 !== 12) startHour24 += 12;
            if (editStartAmPm === 'AM' && startHour24 === 12) startHour24 = 0;
            if (editEndAmPm === 'PM' && endHour24 !== 12) endHour24 += 12;
            if (editEndAmPm === 'AM' && endHour24 === 12) endHour24 = 0;
            
            let totalStartMinutes = startHour24 * 60 + parseInt(editStartMinute);
            let totalEndMinutes = endHour24 * 60 + parseInt(editEndMinute);
            
            const isNextDay = editEndTimeNextDay === true;
            
            if (isNextDay) {
                totalEndMinutes += 1440;
            }
            
            const diffMinutes = totalEndMinutes - totalStartMinutes;
            
            if (diffMinutes > 0) {
                const hours = Math.floor(diffMinutes / 60);
                const minutes = diffMinutes % 60;
                const totalHours = parseFloat((hours + minutes / 60).toFixed(2));
                
                editForm.setFieldsValue({
                    hours: hours,
                    minutes: minutes,
                    duration: totalHours
                });
                setIsEditDurationValid(true);
                
                if (totalHours > 16 && !isNextDay) {
                    message.warning({
                        content: `Duration (${totalHours.toFixed(2)} hrs) is unusually long. If this spans multiple days, please check "End time is on the next day".`,
                        duration: 5,
                    });
                }
            } else if (diffMinutes === 0) {
                editForm.setFieldsValue({
                    hours: 0,
                    minutes: 0,
                    duration: 0
                });
                setIsEditDurationValid(false);
                message.warning('Start and end times are the same. Please adjust.');
            } else {
                editForm.setFieldsValue({
                    hours: 0,
                    minutes: 0,
                    duration: 0
                });
                setIsEditDurationValid(false);
                if (!isNextDay) {
                    message.warning('End time is before start time. If this spans to the next day, check the "End time is on the next day" box.');
                }
            }
        } else {
            editForm.setFieldsValue({
                hours: 0,
                minutes: 0,
                duration: 0
            });
            setIsEditDurationValid(false);
        }
    }, [editStartHour, editStartMinute, editStartAmPm, editEndHour, editEndMinute, editEndAmPm, editEndTimeNextDay, editForm]);

    // ============================================================
    // FETCH USERS
    // ============================================================
    const fetchUsers = useCallback(async () => {
        setLoadingUsers(true);
        try {
            const response = await api.get('/api/users/active-internal-and-partner');
            if (response.data.success) {
                const filteredUsers = response.data.data.filter(user => 
                    user.userType !== 'customer'
                );
                setUsers(filteredUsers);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            message.error('Could not load user list for resources');
        } finally {
            setLoadingUsers(false);
        }
    }, []);

    // ============================================================
    // FETCH TIME ENTRIES
    // ============================================================
    const fetchTimeEntries = useCallback(async () => {
        if (!ticketId) return;
        
        setLoadingEntries(true);
        try {
            const response = await getAllTimeEntriesByTicket(ticketId);
            if (response.success) {
                setEntries(response.data || []);
            }
        } catch (error) {
            console.error('Error fetching time entries:', error);
        } finally {
            setLoadingEntries(false);
        }
    }, [ticketId]);

    // ============================================================
    // FETCH DATA
    // ============================================================
    useEffect(() => {
        const fetchData = async () => {
            if (isDirectMode) {
                if (!ticketId) {
                    message.error('Ticket ID is required for direct time logging');
                    return;
                }
                
                try {
                    await fetchUsers();
                    
                    const ticketResponse = await api.get(`/api/tickets/${ticketId}`);
                    if (ticketResponse.data.success) {
                        setTicketInfo(ticketResponse.data.data);
                    }
                    
                    form.setFieldsValue({
                        cr_master_id: null
                    });
                    
                    await fetchTimeEntries();
                } catch (error) {
                    console.error('Error fetching ticket data:', error);
                    message.error('Failed to load ticket information');
                }
                return;
            }
            
            if (!ticketId && !crMasterId) return;

            try {
                await fetchUsers();
                
                if (crMasterId) {
                    const response = await api.get(`/api/time-tracking/cr/${crMasterId}`);
                    if (response.data.success) {
                        setSelectedCR(response.data.data);
                        setTicketInfo({
                            id: response.data.data.ticket_id,
                            ticket_uuid: response.data.data.ticket_uuid,
                            title: response.data.data.ticket_title
                        });
                        form.setFieldsValue({
                            cr_master_id: crMasterId,
                            cr_number: `${response.data.data.cr_number} - ${response.data.data.description.substring(0, 50)}`
                        });
                    }
                } 
                else if (ticketId) {
                    const ticketResponse = await api.get(`/api/tickets/${ticketId}`);
                    if (ticketResponse.data.success) {
                        setTicketInfo(ticketResponse.data.data);
                    }
                    
                    setLoadingCRs(true);
                    const crResponse = await getCRsByTicket(ticketId);
                    if (crResponse.success) {
                        setCrList(crResponse.data);
                        if (crResponse.data.length > 0) {
                            const firstCR = crResponse.data[0];
                            setSelectedCR(firstCR);
                            form.setFieldsValue({
                                cr_master_id: firstCR.id,
                                cr_number: `${firstCR.cr_number} - ${firstCR.description.substring(0, 50)}`
                            });
                        }
                    }
                    setLoadingCRs(false);
                }
                
                await fetchTimeEntries();
            } catch (error) {
                console.error('Error fetching data:', error);
                message.error('Failed to load ticket information');
            }
        };

        fetchData();
    }, [ticketId, crMasterId, form, fetchUsers, isDirectMode, fetchTimeEntries]);

    const handleCRSelect = (value) => {
        const selected = crList.find(cr => cr.id === value);
        if (selected) {
            setSelectedCR(selected);
            form.setFieldsValue({
                cr_number: `${selected.cr_number} - ${selected.description.substring(0, 50)}`
            });
        }
    };

    // ============================================================
    // SUBMIT HANDLER
    // ============================================================
    const handleSubmit = async (values) => {
        if (loading) return;
        
        if (!values.duration || values.duration <= 0) {
            message.error('Cannot save time entry with 0 duration. Please check your start and end times.');
            return;
        }
        
        setLoading(true);
        
        const resources = Array.isArray(values.resource) 
            ? values.resource.join(', ') 
            : values.resource;
        
        let startTimeStr = null;
        let endTimeStr = null;
        
        if (values.start_hour !== undefined && values.start_minute !== undefined && values.start_ampm) {
            let hour = parseInt(values.start_hour);
            if (values.start_ampm === 'PM' && hour !== 12) hour += 12;
            if (values.start_ampm === 'AM' && hour === 12) hour = 0;
            startTimeStr = `${String(hour).padStart(2, '0')}:${String(values.start_minute).padStart(2, '0')}:00`;
        }
        
        if (values.end_hour !== undefined && values.end_minute !== undefined && values.end_ampm) {
            let hour = parseInt(values.end_hour);
            if (values.end_ampm === 'PM' && hour !== 12) hour += 12;
            if (values.end_ampm === 'AM' && hour === 12) hour = 0;
            endTimeStr = `${String(hour).padStart(2, '0')}:${String(values.end_minute).padStart(2, '0')}:00`;
        }
        
        try {
            let response;
            
            if (isDirectMode) {
                const payload = {
                    ticket_id: parseInt(ticketId),
                    entry_date: values.entry_date ? values.entry_date.format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
                    start_time: startTimeStr,
                    end_time: endTimeStr,
                    duration: values.duration,
                    activity_type: values.activity_type,
                    resource: resources,
                    description: values.description || null
                };
                
                response = await addDirectTimeEntry(payload);
            } else {
                const payload = {
                    cr_master_id: values.cr_master_id,
                    entry_date: values.entry_date ? values.entry_date.format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
                    start_time: startTimeStr,
                    end_time: endTimeStr,
                    duration: values.duration,
                    activity_type: values.activity_type,
                    resource: resources,
                    description: values.description || null
                };
                
                response = await addTimeEntry(payload);
            }
            
            if (response.success) {
                setSavedCount(prev => prev + 1);
                await fetchTimeEntries();
                
                const shouldClose = shouldCloseAfterSave;
                setShouldCloseAfterSave(false);
                
                if (shouldClose) {
                    message.success(`Time entry saved successfully! (${savedCount + 1} total)`);
                    setTimeout(() => {
                        navigate(-1);
                    }, 500);
                } else {
                    message.success(`Time entry saved! (${savedCount + 1} so far)`);
                    
                    form.setFieldsValue({
                        entry_date: null,
                        start_hour: undefined,
                        start_minute: undefined,
                        start_ampm: undefined,
                        end_hour: undefined,
                        end_minute: undefined,
                        end_ampm: undefined,
                        end_time_next_day: false,
                        duration: 0,
                        hours: 0,
                        minutes: 0,
                        activity_type: undefined,
                        resource: undefined,
                        description: undefined
                    });
                    setIsDurationValid(false);
                    
                    if (!isDirectMode && selectedCR) {
                        form.setFieldsValue({
                            cr_master_id: selectedCR.id,
                            cr_number: `${selectedCR.cr_number} - ${selectedCR.description.substring(0, 50)}`
                        });
                    }
                    
                    if (!isDirectMode && ticketId) {
                        const crResponse = await getCRsByTicket(ticketId);
                        if (crResponse.success) {
                            setCrList(crResponse.data);
                        }
                    }
                }
            } else {
                message.error(response.message || 'Failed to add time entry');
                setShouldCloseAfterSave(false);
            }
        } catch (error) {
            console.error('Error adding time entry:', error);
            message.error(error.response?.data?.message || 'Failed to add time entry');
            setShouldCloseAfterSave(false);
        } finally {
            setLoading(false);
        }
    };

    // ============================================================
    // EDIT TIME ENTRY
    // ============================================================
    const handleEditEntry = (entry) => {
        setEditingEntry(entry);
        
        let startHour = null, startMinute = null, startAmPm = null;
        let endHour = null, endMinute = null, endAmPm = null;
        let isNextDay = false;
        
        if (entry.start_time) {
            const startMoment = moment(entry.start_time, 'HH:mm:ss');
            if (startMoment.isValid()) {
                const h = startMoment.hour();
                startHour = h % 12 || 12;
                startMinute = startMoment.minute();
                startAmPm = h >= 12 ? 'PM' : 'AM';
            }
        }
        
        if (entry.end_time) {
            const endMoment = moment(entry.end_time, 'HH:mm:ss');
            if (endMoment.isValid()) {
                const h = endMoment.hour();
                endHour = h % 12 || 12;
                endMinute = endMoment.minute();
                endAmPm = h >= 12 ? 'PM' : 'AM';
            }
        }
        
        const duration = parseFloat(entry.duration) || 0;
        const hours = Math.floor(duration);
        const minutes = Math.round((duration - hours) * 60);
        
        // Only auto-detect next day if duration is valid (> 0)
        if (duration > 0 && startHour !== null && startMinute !== null && startAmPm && endHour !== null && endMinute !== null && endAmPm) {
            let startHour24 = parseInt(startHour);
            let endHour24 = parseInt(endHour);
            
            if (startAmPm === 'PM' && startHour24 !== 12) startHour24 += 12;
            if (startAmPm === 'AM' && startHour24 === 12) startHour24 = 0;
            if (endAmPm === 'PM' && endHour24 !== 12) endHour24 += 12;
            if (endAmPm === 'AM' && endHour24 === 12) endHour24 = 0;
            
            const totalStartMinutes = startHour24 * 60 + parseInt(startMinute);
            const totalEndMinutes = endHour24 * 60 + parseInt(endMinute);
            
            if (totalEndMinutes < totalStartMinutes) {
                isNextDay = true;
            }
        }
        
        let resourceArray = [];
        if (entry.resource) {
            resourceArray = entry.resource.split(', ').filter(r => r.trim() !== '');
        }
        
        editForm.setFieldsValue({
            entry_date: entry.entry_date ? moment(entry.entry_date) : null,
            start_hour: startHour,
            start_minute: startMinute,
            start_ampm: startAmPm,
            end_hour: endHour,
            end_minute: endMinute,
            end_ampm: endAmPm,
            end_time_next_day: isNextDay,
            duration: duration,
            hours: hours,
            minutes: minutes,
            activity_type: entry.activity_type,
            resource: resourceArray,
            description: entry.description || ''
        });
        setIsEditDurationValid(duration > 0);
        
        setEditModalVisible(true);
    };

    // ============================================================
    // UPDATE TIME ENTRY
    // ============================================================
    const handleUpdateEntry = async (values) => {
        if (!editingEntry) return;
        
        if (!values.duration || values.duration <= 0) {
            message.error('Cannot update time entry with 0 duration. Please check your start and end times.');
            return;
        }
        
        setEditLoading(true);
        
        const resources = Array.isArray(values.resource) 
            ? values.resource.join(', ') 
            : values.resource;
        
        let startTimeStr = null;
        let endTimeStr = null;
        
        if (values.start_hour !== undefined && values.start_minute !== undefined && values.start_ampm) {
            let hour = parseInt(values.start_hour);
            if (values.start_ampm === 'PM' && hour !== 12) hour += 12;
            if (values.start_ampm === 'AM' && hour === 12) hour = 0;
            startTimeStr = `${String(hour).padStart(2, '0')}:${String(values.start_minute).padStart(2, '0')}:00`;
        }
        
        if (values.end_hour !== undefined && values.end_minute !== undefined && values.end_ampm) {
            let hour = parseInt(values.end_hour);
            if (values.end_ampm === 'PM' && hour !== 12) hour += 12;
            if (values.end_ampm === 'AM' && hour === 12) hour = 0;
            endTimeStr = `${String(hour).padStart(2, '0')}:${String(values.end_minute).padStart(2, '0')}:00`;
        }
        
        const payload = {
            entry_date: values.entry_date ? values.entry_date.format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
            start_time: startTimeStr,
            end_time: endTimeStr,
            duration: values.duration,
            activity_type: values.activity_type,
            resource: resources,
            description: values.description || null
        };
        
        try {
            const response = await updateTimeEntry(editingEntry.id, payload);
            if (response.success) {
                message.success('Time entry updated successfully!');
                setEditModalVisible(false);
                setEditingEntry(null);
                await fetchTimeEntries();
                editForm.resetFields();
                setIsEditDurationValid(false);
            } else {
                message.error(response.message || 'Failed to update time entry');
            }
        } catch (error) {
            console.error('Error updating time entry:', error);
            message.error(error.response?.data?.message || 'Failed to update time entry');
        } finally {
            setEditLoading(false);
        }
    };

    // ============================================================
    // DELETE TIME ENTRY
    // ============================================================
    const handleDeleteEntry = async (entryId) => {
        try {
            const response = await deleteTimeEntry(entryId);
            if (response.success) {
                message.success('Time entry deleted successfully!');
                await fetchTimeEntries();
            } else {
                message.error(response.message || 'Failed to delete time entry');
            }
        } catch (error) {
            console.error('Error deleting time entry:', error);
            message.error(error.response?.data?.message || 'Failed to delete time entry');
        }
    };

    const handleManageActivities = () => {
        navigate(`/time-tracking/cr?ticketId=${ticketId}`);
    };

    const handleFormSubmit = (values) => {
        handleSubmit(values);
    };

    const handleSaveAndAddAnother = () => {
        setShouldCloseAfterSave(false);
        form.submit();
    };

    const handleSaveAndClose = () => {
        setShouldCloseAfterSave(true);
        form.submit();
    };

    // ============================================================
    // TABLE COLUMNS
    // ============================================================
    const columns = [
        {
            title: 'Date',
            dataIndex: 'entry_date',
            key: 'entry_date',
            width: 120,
            render: (date) => date ? moment(date).format('DD/MM/YYYY') : '-'
        },
        {
            title: 'Start',
            dataIndex: 'start_time',
            key: 'start_time',
            width: 80,
            render: (time) => time ? moment(time, 'HH:mm:ss').format('hh:mm A') : '-'
        },
        {
            title: 'End',
            dataIndex: 'end_time',
            key: 'end_time',
            width: 80,
            render: (time) => time ? moment(time, 'HH:mm:ss').format('hh:mm A') : '-'
        },
        {
            title: 'Hours',
            dataIndex: 'duration',
            key: 'duration',
            width: 80,
            align: 'center',
            render: (value) => `${parseFloat(value).toFixed(2)} hrs`
        },
        {
            title: 'Activity',
            dataIndex: 'activity_type',
            key: 'activity_type',
            width: 150,
            ellipsis: true,
        },
        {
            title: 'Resource(s)',
            dataIndex: 'resource',
            key: 'resource',
            width: 150,
            ellipsis: true,
            render: (text) => text || '-'
        },
        {
            title: 'Source',
            dataIndex: 'cr_master_id',
            key: 'source',
            width: 100,
            render: (crMasterId) => (
                <Tag color={crMasterId ? 'blue' : 'green'}>
                    {crMasterId ? 'CR' : 'Direct'}
                </Tag>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 120,
            fixed: 'right',
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="Edit">
                        <Button 
                            type="link" 
                            icon={<EditOutlined />} 
                            onClick={() => handleEditEntry(record)}
                            size="small"
                        />
                    </Tooltip>
                    <Popconfirm
                        title="Delete this time entry?"
                        description="This action cannot be undone."
                        onConfirm={() => handleDeleteEntry(record.id)}
                        okText="Yes, Delete"
                        okType="danger"
                        cancelText="Cancel"
                    >
                        <Tooltip title="Delete">
                            <Button 
                                type="link" 
                                danger 
                                icon={<DeleteOutlined />}
                                size="small"
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    if (!ticketId && !crMasterId) {
        return (
            <div className="time-tracking-page">
                <Card className="time-tracking-card">
                    <div className="empty-state">
                        <Title level={4}>No Ticket Selected</Title>
                        <Text type="secondary">Please select a ticket from the ticket list or edit ticket page to log time.</Text>
                        <div style={{ marginTop: 20 }}>
                            <Button onClick={() => navigate('/tickets')}>Go to Tickets</Button>
                        </div>
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div className="time-tracking-page">
            <div className="time-tracking-header">
                <Title level={2} className="time-tracking-title">
                    <ClockCircleOutlined /> 
                    {isDirectMode ? 'Log Time (No CR Required)' : 'Log Time Entry'}
                </Title>
                <Text className="time-tracking-description">
                    {isDirectMode 
                        ? 'Record your work hours directly to this ticket. No CR/CM is required.'
                        : 'Record your daily work hours for this activity. Select hours, minutes, and AM/PM for start and end times. Duration will auto-calculate.'
                    }
                </Text>
            </div>

            <Card className="time-tracking-card">
                <div className="time-tracking-content">
                    {ticketInfo && (
                        <div className="cr-info-box">
                            <div className="cr-info-title">Ticket Information</div>
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Text type="secondary">Ticket Number:</Text>
                                    <div>
                                        <Text strong>
                                            {ticketInfo.ticket_uuid || 
                                             ticketInfo.ticketUuid || 
                                             ticketInfo.uuid || 
                                             ticketInfo.ticketNumber ||
                                             ticketInfo.ticket_number ||
                                             'N/A'}
                                        </Text>
                                    </div>
                                </Col>
                                <Col span={16}>
                                    <Text type="secondary">Title:</Text>
                                    <div>
                                        <Text strong>
                                            {ticketInfo.title || 
                                             ticketInfo.Title || 
                                             'N/A'}
                                        </Text>
                                    </div>
                                </Col>
                            </Row>
                            {savedCount > 0 && (
                                <div style={{ 
                                    marginTop: 12, 
                                    paddingTop: 12, 
                                    borderTop: '1px solid #e8e8e8',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8
                                }}>
                                    <Badge 
                                        count={savedCount} 
                                        style={{ backgroundColor: '#52c41a' }}
                                    />
                                    <Text type="secondary">
                                        {savedCount} time entr{savedCount > 1 ? 'ies' : 'y'} saved in this session
                                    </Text>
                                </div>
                            )}
                        </div>
                    )}

                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleFormSubmit}
                        initialValues={{
                            entry_date: null,
                            duration: 0,
                            hours: 0,
                            minutes: 0,
                            start_hour: undefined,
                            start_minute: undefined,
                            start_ampm: undefined,
                            end_hour: undefined,
                            end_minute: undefined,
                            end_ampm: undefined,
                            end_time_next_day: false
                        }}
                    >
                        {!isDirectMode && (
                            <>
                                <Form.Item
                                    name="cr_master_id"
                                    label="Activity"
                                    rules={[{ required: true, message: 'Please select an activity' }]}
                                >
                                    <Select
                                        placeholder="Select Activity"
                                        loading={loadingCRs}
                                        onChange={handleCRSelect}
                                        disabled={!!crMasterId}
                                        showSearch
                                        optionFilterProp="children"
                                        className="form-control"
                                    >
                                        {crList.map(cr => (
                                            <Option key={cr.id} value={cr.id}>
                                                {cr.cr_number} - {cr.description.substring(0, 60)}...
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>

                                {selectedCR && (
                                    <div style={{ 
                                        background: '#f6ffed', 
                                        padding: '12px 16px', 
                                        borderRadius: 8, 
                                        marginBottom: 16,
                                        border: '1px solid #b7eb8f'
                                    }}>
                                        <Row gutter={16}>
                                            <Col span={8}>
                                                <Text type="secondary">Request Date:</Text>
                                                <div><Text strong>{selectedCR.request_date ? moment(selectedCR.request_date).format('DD/MM/YYYY') : 'N/A'}</Text></div>
                                            </Col>
                                            <Col span={16}>
                                                <Text type="secondary">Description:</Text>
                                                <div><Text strong>{selectedCR.description}</Text></div>
                                            </Col>
                                        </Row>
                                    </div>
                                )}
                            </>
                        )}

                        {isDirectMode && (
                            <Alert
                                message="Direct Time Logging"
                                description="You are logging time directly to this ticket without creating a CR/CM. This time will appear in reports but will not be linked to any CR/CM."
                                type="info"
                                showIcon
                                style={{ marginBottom: 16 }}
                            />
                        )}

                        {isDirectMode && (
                            <Form.Item name="cr_master_id" hidden initialValue={null}>
                                <Input />
                            </Form.Item>
                        )}

                        <Form.Item name="cr_number" hidden>
                            <Input />
                        </Form.Item>

                        <Divider />

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="entry_date"
                                    label="Entry Date"
                                    rules={[{ required: true, message: 'Please select date' }]}
                                >
                                    <DatePicker 
                                        style={{ width: '100%' }} 
                                        format="DD/MM/YYYY"
                                        className="form-control"
                                        placeholder="Select date"
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="activity_type"
                                    label="Type of Activity"
                                    rules={[{ required: true, message: 'Please select activity type' }]}
                                >
                                    <Select placeholder="Select activity type" className="form-control">
                                        <Option value="Analysis">Analysis</Option>
                                        <Option value="Bug Fixes">Bug Fixes</Option>
                                        <Option value="Data Patching">Data Patching</Option>
                                        <Option value="Deployment">Deployment</Option>
                                        <Option value="Development">Development</Option>                                    
                                        <Option value="Documentation">Documentation</Option>
                                        <Option value="Final Acceptance Test">FAT</Option>
                                        <Option value="Infra Setup & Configuration">Infra Setup & Configuration</Option>
                                        <Option value="Meeting">Meeting</Option>
                                        <Option value="Patch Upgrade">Patch Upgrade</Option>
                                        <Option value="Review">Review</Option>
                                        <Option value="Security Hardening">Security Hardening</Option>
                                        <Option value="System Integration Test">SIT</Option>
                                        <Option value="Support">Support</Option>
                                        <Option value="Testing">Testing</Option>
                                        <Option value="User Acceptance Test">UAT</Option>
                                        <Option value="URS">User Requirement Study / User Story</Option>
                                        <Option value="Other">Other</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>

                        {/* START TIME */}
                        <div style={{ marginBottom: 8 }}>
                            <Text strong>Start Time</Text>
                        </div>
                        <Row gutter={8}>
                            <Col span={8}>
                                <Form.Item
                                    name="start_hour"
                                    rules={[{ required: true, message: 'Required' }]}
                                >
                                    <Select placeholder="HH" className="form-control" size="large">
                                        {Array.from({ length: 12 }, (_, i) => i + 1).map(h => (
                                            <Option key={h} value={h}>{String(h).padStart(2, '0')}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={1} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Text strong>:</Text>
                            </Col>
                            <Col span={7}>
                                <Form.Item
                                    name="start_minute"
                                    rules={[{ required: true, message: 'Required' }]}
                                >
                                    <Select placeholder="MM" className="form-control" size="large">
                                        {Array.from({ length: 12 }, (_, i) => i * 5).map(m => (
                                            <Option key={m} value={m}>{String(m).padStart(2, '0')}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    name="start_ampm"
                                    rules={[{ required: true, message: 'Required' }]}
                                >
                                    <Select placeholder="AM/PM" className="form-control" size="large">
                                        <Option value="AM">AM</Option>
                                        <Option value="PM">PM</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>

                        {/* END TIME */}
                        <div style={{ marginBottom: 8, marginTop: 8 }}>
                            <Text strong>End Time</Text>
                        </div>
                        <Row gutter={8}>
                            <Col span={8}>
                                <Form.Item
                                    name="end_hour"
                                    rules={[{ required: true, message: 'Required' }]}
                                >
                                    <Select placeholder="HH" className="form-control" size="large">
                                        {Array.from({ length: 12 }, (_, i) => i + 1).map(h => (
                                            <Option key={h} value={h}>{String(h).padStart(2, '0')}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={1} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Text strong>:</Text>
                            </Col>
                            <Col span={7}>
                                <Form.Item
                                    name="end_minute"
                                    rules={[{ required: true, message: 'Required' }]}
                                >
                                    <Select placeholder="MM" className="form-control" size="large">
                                        {Array.from({ length: 12 }, (_, i) => i * 5).map(m => (
                                            <Option key={m} value={m}>{String(m).padStart(2, '0')}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    name="end_ampm"
                                    rules={[{ required: true, message: 'Required' }]}
                                >
                                    <Select placeholder="AM/PM" className="form-control" size="large">
                                        <Option value="AM">AM</Option>
                                        <Option value="PM">PM</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>

                        {/* NEXT DAY CHECKBOX */}
                        <Row gutter={16} style={{ marginTop: 8 }}>
                            <Col span={24}>
                                <Form.Item name="end_time_next_day" valuePropName="checked">
                                    <Checkbox>
                                        End time is on the <strong>next day</strong>
                                    </Checkbox>
                                </Form.Item>
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                    Check this box if the end time is after midnight (e.g., Start 10 PM → End 2 AM next day)
                                </Text>
                            </Col>
                        </Row>

                        {/* CALCULATED DURATION */}
                        <div style={{ marginBottom: 8, marginTop: 16 }}>
                            <Text strong>Calculated Duration</Text>
                        </div>
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item name="hours" label="Hours">
                                    <InputNumber 
                                        style={{ width: '100%' }} 
                                        className="form-control"
                                        size="large"
                                        disabled
                                        placeholder="Auto"
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name="minutes" label="Minutes">
                                    <InputNumber 
                                        style={{ width: '100%' }} 
                                        className="form-control"
                                        size="large"
                                        disabled
                                        placeholder="Auto"
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    name="duration"
                                    label="Duration (hours)"
                                    rules={[{ required: true, message: 'Duration is required' }]}
                                >
                                    <InputNumber 
                                        style={{ width: '100%' }} 
                                        className="form-control"
                                        size="large"
                                        disabled
                                        placeholder="Auto-calculated"
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={24}>
                                <Form.Item
                                    name="resource"
                                    label="Resource(s) *"
                                    rules={[{ required: true, message: 'Please select at least one resource' }]}
                                >
                                    <Select
                                        mode="multiple"
                                        placeholder="Select one or more resources"
                                        loading={loadingUsers}
                                        showSearch
                                        optionFilterProp="children"
                                        allowClear
                                        className="form-control"
                                        size="large"
                                        maxTagCount="responsive"
                                    >
                                        {users.map(user => (
                                            <Option key={user.id} value={`${user.firstName} ${user.lastName}`}>
                                                {user.firstName} {user.lastName} - {user.email}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item name="description" label="Description">
                            <Input.TextArea rows={3} placeholder="Describe the work done..." className="form-control" />
                        </Form.Item>

                        <Divider />

                        <div className="form-actions">
                            <Space>
                                <Button 
                                    icon={<PlusOutlined />} 
                                    onClick={handleManageActivities}
                                >
                                    Manage Activities
                                </Button>
                                <Button icon={<RollbackOutlined />} onClick={() => navigate(-1)}>
                                    Back
                                </Button>
                            </Space>
                            <Space>
                                <Button 
                                    type="default"
                                    icon={<PlusOutlined />}
                                    loading={loading}
                                    onClick={handleSaveAndAddAnother}
                                    disabled={!isDurationValid}
                                >
                                    Save & Add Another
                                </Button>
                                <Button 
                                    type="primary" 
                                    icon={<SaveOutlined />}
                                    loading={loading}
                                    className="submit-button"
                                    onClick={handleSaveAndClose}
                                    disabled={!isDurationValid}
                                >
                                    Save & Close
                                </Button>
                            </Space>
                        </div>
                        {!isDurationValid && (
                            <div style={{ marginTop: 8, color: '#ff4d4f' }}>
                                <Text type="danger">
                                    ⚠️ Please set valid start and end times before saving. Duration must be greater than 0.
                                </Text>
                            </div>
                        )}
                    </Form>
                </div>
            </Card>

            {/* Manage Existing Time Entries */}
            <Card 
                className="time-tracking-card" 
                style={{ marginTop: 16 }}
                title={
                    <Space>
                        <FileTextOutlined />
                        <span>Manage Time Entries</span>
                        <Badge count={entries.length} style={{ backgroundColor: '#1890ff' }} />
                    </Space>
                }
                extra={
                    <Button 
                        icon={<ReloadOutlined />} 
                        onClick={fetchTimeEntries}
                        loading={loadingEntries}
                        size="small"
                    >
                        Refresh
                    </Button>
                }
            >
                {entries.length === 0 ? (
                    <div className="empty-state" style={{ padding: '20px 0' }}>
                        <Text type="secondary">No time entries found for this ticket.</Text>
                    </div>
                ) : (
                    <Table
                        columns={columns}
                        dataSource={entries}
                        rowKey="id"
                        pagination={{ pageSize: 10 }}
                        scroll={{ x: 900 }}
                        loading={loadingEntries}
                        size="middle"
                    />
                )}
            </Card>

            {/* Edit Modal */}
            <Modal
                title="Edit Time Entry"
                open={editModalVisible}
                onCancel={() => {
                    setEditModalVisible(false);
                    setEditingEntry(null);
                    editForm.resetFields();
                    setIsEditDurationValid(false);
                }}
                footer={null}
                width={700}
                destroyOnClose
            >
                <Form
                    form={editForm}
                    layout="vertical"
                    onFinish={handleUpdateEntry}
                    initialValues={{
                        duration: 0,
                        hours: 0,
                        minutes: 0,
                        start_hour: undefined,
                        start_minute: undefined,
                        start_ampm: undefined,
                        end_hour: undefined,
                        end_minute: undefined,
                        end_ampm: undefined,
                        end_time_next_day: false
                    }}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="entry_date"
                                label="Entry Date"
                                rules={[{ required: true, message: 'Please select date' }]}
                            >
                                <DatePicker 
                                    style={{ width: '100%' }} 
                                    format="DD/MM/YYYY"
                                    placeholder="Select date"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="activity_type"
                                label="Type of Activity"
                                rules={[{ required: true, message: 'Please select activity type' }]}
                            >
                                <Select placeholder="Select activity type">
                                    <Option value="Analysis">Analysis</Option>
                                    <Option value="Bug Fixes">Bug Fixes</Option>
                                    <Option value="Data Patching">Data Patching</Option>
                                    <Option value="Deployment">Deployment</Option>
                                    <Option value="Development">Development</Option>                                    
                                    <Option value="Documentation">Documentation</Option>
                                    <Option value="Final Acceptance Test">FAT</Option>
                                    <Option value="Infra Setup & Configuration">Infra Setup & Configuration</Option>
                                    <Option value="Meeting">Meeting</Option>
                                    <Option value="Patch Upgrade">Patch Upgrade</Option>
                                    <Option value="Review">Review</Option>
                                    <Option value="Security Hardening">Security Hardening</Option>
                                    <Option value="System Integration Test">SIT</Option>
                                    <Option value="Support">Support</Option>
                                    <Option value="Testing">Testing</Option>
                                    <Option value="User Acceptance Test">UAT</Option>
                                    <Option value="URS">User Requirement Study / User Story</Option>
                                    <Option value="Other">Other</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* START TIME */}
                    <div style={{ marginBottom: 8 }}>
                        <Text strong>Start Time</Text>
                    </div>
                    <Row gutter={8}>
                        <Col span={8}>
                            <Form.Item
                                name="start_hour"
                                rules={[{ required: true, message: 'Required' }]}
                            >
                                <Select placeholder="HH" size="large">
                                    {Array.from({ length: 12 }, (_, i) => i + 1).map(h => (
                                        <Option key={h} value={h}>{String(h).padStart(2, '0')}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={1} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Text strong>:</Text>
                        </Col>
                        <Col span={7}>
                            <Form.Item
                                name="start_minute"
                                rules={[{ required: true, message: 'Required' }]}
                            >
                                <Select placeholder="MM" size="large">
                                    {Array.from({ length: 12 }, (_, i) => i * 5).map(m => (
                                        <Option key={m} value={m}>{String(m).padStart(2, '0')}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="start_ampm"
                                rules={[{ required: true, message: 'Required' }]}
                            >
                                <Select placeholder="AM/PM" size="large">
                                    <Option value="AM">AM</Option>
                                    <Option value="PM">PM</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* END TIME */}
                    <div style={{ marginBottom: 8, marginTop: 8 }}>
                        <Text strong>End Time</Text>
                    </div>
                    <Row gutter={8}>
                        <Col span={8}>
                            <Form.Item
                                name="end_hour"
                                rules={[{ required: true, message: 'Required' }]}
                            >
                                <Select placeholder="HH" size="large">
                                    {Array.from({ length: 12 }, (_, i) => i + 1).map(h => (
                                        <Option key={h} value={h}>{String(h).padStart(2, '0')}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={1} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Text strong>:</Text>
                        </Col>
                        <Col span={7}>
                            <Form.Item
                                name="end_minute"
                                rules={[{ required: true, message: 'Required' }]}
                            >
                                <Select placeholder="MM" size="large">
                                    {Array.from({ length: 12 }, (_, i) => i * 5).map(m => (
                                        <Option key={m} value={m}>{String(m).padStart(2, '0')}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="end_ampm"
                                rules={[{ required: true, message: 'Required' }]}
                            >
                                <Select placeholder="AM/PM" size="large">
                                    <Option value="AM">AM</Option>
                                    <Option value="PM">PM</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* NEXT DAY CHECKBOX */}
                    <Row gutter={16} style={{ marginTop: 8 }}>
                        <Col span={24}>
                            <Form.Item name="end_time_next_day" valuePropName="checked">
                                <Checkbox>
                                    End time is on the <strong>next day</strong>
                                </Checkbox>
                            </Form.Item>
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                Check this box if the end time is after midnight (e.g., Start 10 PM → End 2 AM next day)
                            </Text>
                        </Col>
                    </Row>

                    {/* CALCULATED DURATION */}
                    <div style={{ marginBottom: 8, marginTop: 16 }}>
                        <Text strong>Calculated Duration</Text>
                    </div>
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="hours" label="Hours">
                                <InputNumber 
                                    style={{ width: '100%' }} 
                                    size="large"
                                    disabled
                                    placeholder="Auto"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="minutes" label="Minutes">
                                <InputNumber 
                                    style={{ width: '100%' }} 
                                    size="large"
                                    disabled
                                    placeholder="Auto"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="duration"
                                label="Duration (hours)"
                                rules={[{ required: true, message: 'Duration is required' }]}
                            >
                                <InputNumber 
                                    style={{ width: '100%' }} 
                                    size="large"
                                    disabled
                                    placeholder="Auto-calculated"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                name="resource"
                                label="Resource(s) *"
                                rules={[{ required: true, message: 'Please select at least one resource' }]}
                            >
                                <Select
                                    mode="multiple"
                                    placeholder="Select one or more resources"
                                    loading={loadingUsers}
                                    showSearch
                                    optionFilterProp="children"
                                    allowClear
                                    size="large"
                                    maxTagCount="responsive"
                                >
                                    {users.map(user => (
                                        <Option key={user.id} value={`${user.firstName} ${user.lastName}`}>
                                            {user.firstName} {user.lastName} - {user.email}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="description" label="Description">
                        <Input.TextArea rows={3} placeholder="Describe the work done..." />
                    </Form.Item>

                    <Divider />

                    <div className="form-actions" style={{ marginTop: 0, paddingTop: 0, borderTop: 'none' }}>
                        <Button onClick={() => {
                            setEditModalVisible(false);
                            setEditingEntry(null);
                            editForm.resetFields();
                            setIsEditDurationValid(false);
                        }}>
                            Cancel
                        </Button>
                        <Button 
                            type="primary" 
                            htmlType="submit" 
                            loading={editLoading}
                            icon={<SaveOutlined />}
                            disabled={!isEditDurationValid}
                        >
                            Update Entry
                        </Button>
                    </div>
                    {!isEditDurationValid && (
                        <div style={{ marginTop: 8, color: '#ff4d4f' }}>
                            <Text type="danger">
                                ⚠️ Please set valid start and end times before updating. Duration must be greater than 0.
                            </Text>
                        </div>
                    )}
                </Form>
            </Modal>
        </div>
    );
};

export default TimeEntryForm;